/**
 * TransactionJourney.js — Unified Transaction Journey Dashboard.
 * Shows all active transactions for a buyer with drill-down to full timeline,
 * stage stepper, milestones, and quick navigation to related features.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import './TransactionJourney.css';
import {
  useJourneyDetail,
  useJourneyMilestones,
  updateMilestone,
  initializeJourney,
  usePropertyJourneys,
  useSellerJourneys,
} from '../../agents/journeyAgent';
import CondoDocumentsPanel from './CondoDocumentsPanel';
import ContingencyDeadlineReviewNotice from './ContingencyDeadlineReviewNotice';
import { resolveViewerRole } from './condoDocumentsModel';
import { parseDateOnly } from '../OfferManagement/utils';

/**
 * Whether the § 703.33 panel belongs on this transaction. The journey payload's
 * `condo_documents: {applies, clock, warnings}` (arch §8.5) is authoritative;
 * a payload without it falls back to the house's paperwork category. The
 * panel itself also hides on a 404.
 */
export function condoDocumentsApply(journey) {
  if (!journey) return false;
  if (journey.condo_documents) return journey.condo_documents.applies === true;
  return journey.property?.paperwork_category === 'condo';
}

const STAGES = [
  { key: 'listing_saved', label: 'Listing Saved', icon: '🏠' },
  { key: 'tour_scheduled', label: 'Tour Scheduled', icon: '📅' },
  { key: 'tour_completed', label: 'Tour Completed', icon: '✅' },
  { key: 'offer_submitted', label: 'Offer Submitted', icon: '📝' },
  { key: 'offer_accepted', label: 'Offer Accepted', icon: '🤝' },
  { key: 'contingencies', label: 'Contingencies', icon: '🔍' },
  { key: 'clear_to_close', label: 'Clear to Close', icon: '✅' },
  { key: 'final_walkthrough', label: 'Final Walkthrough', icon: '🚶' },
  { key: 'closing_day', label: 'Closing Day', icon: '🔑' },
  { key: 'completed', label: 'Completed!', icon: '🎉' },
];

function getStageIndex(stageKey) {
  const idx = STAGES.findIndex((s) => s.key === stageKey);
  return idx >= 0 ? idx : 0;
}

const STATUS_CYCLE = ['pending', 'in_progress', 'completed', 'skipped'];
const JOURNEYS_PER_PAGE = 9;

function getJourneySortTimestamp(journey) {
  const candidates = [
    journey?.first_interaction_at,
    journey?.tour?.created_at,
    journey?.offer?.created_at,
    ...(Array.isArray(journey?.timeline_events)
      ? journey.timeline_events.map((event) => event?.date)
      : []),
  ]
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value));

  if (candidates.length === 0) {
    return 0;
  }

  return Math.max(...candidates);
}

// ---------------------------------------------------------------------------
// Stage Stepper
// ---------------------------------------------------------------------------
function StageStepper({ currentStage }) {
  const activeIdx = getStageIndex(currentStage);

  return (
    <>
      {/* Full stepper — hidden on mobile via CSS */}
      <div className="stage-stepper" role="list" aria-label="Transaction stages" aria-hidden="false">
        {STAGES.map((stage, i) => {
          let state = 'pending';
          if (i < activeIdx) state = 'completed';
          else if (i === activeIdx) state = 'active';

          return (
            <React.Fragment key={stage.key}>
              {i > 0 && (
                <div
                  className={`stage-connector ${i <= activeIdx ? 'filled' : ''}`}
                  aria-hidden="true"
                />
              )}
              <div
                className={`stage-step ${state}`}
                role="listitem"
                aria-current={state === 'active' ? 'step' : undefined}
              >
                <span className="stage-circle">
                  {state === 'completed' ? '✓' : stage.icon}
                </span>
                <span className="stage-label">{stage.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Condensed step counter — shown only on mobile via CSS */}
      <div className="stepper-mobile-label" aria-live="polite">
        Step {activeIdx + 1} of {STAGES.length} — {STAGES[activeIdx]?.label}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Next Action Banner
// ---------------------------------------------------------------------------
export function NextActionBanner({ nextAction }) {
  if (!nextAction) return null;
  return (
    <div className="next-action-banner" role="status">
      <span className="next-action-icon">⚡</span>
      <div className="next-action-content">
        <h3 className="next-action-title">Next Step</h3>
        <p className="next-action-text">{nextAction}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress Card
// ---------------------------------------------------------------------------
function ProgressCard({ label, completed, total, accentClass }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className={`progress-card ${accentClass || ''}`}>
      <span className="progress-card-label">{label}</span>
      <div className="progress-bar-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="progress-card-stat">
        {completed}/{total} <span className="progress-card-pct">({pct}%)</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Milestone Timeline
// ---------------------------------------------------------------------------
export function MilestoneTimeline({ milestones, updatingId, onToggle, onInitialize, initializing }) {
  if (!milestones || milestones.length === 0) {
    return (
      <div className="milestone-empty-wrap">
        <p className="milestone-empty">No milestones yet.</p>
        {onInitialize && (
          <button
            className="milestone-init-btn"
            onClick={onInitialize}
            disabled={initializing}
            type="button"
          >
            {initializing ? 'Setting up…' : '✦ Set up milestone timeline'}
          </button>
        )}
      </div>
    );
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return (
    <div className="milestone-timeline" role="list" aria-label="Milestones">
      {milestones.map((m) => {
        const statusClass =
          m.status === 'completed'
            ? 'completed'
            : m.status === 'in_progress'
            ? 'in-progress'
            : m.status === 'skipped'
            ? 'skipped'
            : 'pending';
        const isUpdating = updatingId === m.id;
        const displayDate = m.status === 'completed' && m.completed_date
          ? m.completed_date
          : m.target_date;
        // Date-only target: "overdue" means the due day is before today (parsed
        // at local midnight), so a milestone due today isn't flagged early.
        const dueDate = parseDateOnly(displayDate);
        const isOverdue = Boolean(dueDate) && dueDate < startOfToday && m.status !== 'completed' && m.status !== 'skipped';

        return (
          <button
            key={m.id}
            className={`milestone-item ${statusClass}${isUpdating ? ' updating' : ''}`}
            onClick={() => !isUpdating && onToggle(m)}
            type="button"
            disabled={isUpdating}
            aria-label={`${m.title} — ${m.status.replace('_', ' ')}. Click to mark as ${STATUS_CYCLE[(STATUS_CYCLE.indexOf(m.status) + 1) % STATUS_CYCLE.length].replace('_', ' ')}.`}
            title={`Mark as ${STATUS_CYCLE[(STATUS_CYCLE.indexOf(m.status) + 1) % STATUS_CYCLE.length].replace('_', ' ')} →`}
          >
            <span className="milestone-dot" aria-hidden="true">
              {m.status === 'completed' && <span className="milestone-check">✓</span>}
              {m.status === 'skipped' && <span className="milestone-check skipped-x">–</span>}
              {isUpdating && <span className="milestone-updating-spinner" />}
            </span>
            <div className="milestone-body">
              <span className="milestone-title">{m.title}</span>
              {displayDate && (
                <span className={`milestone-date ${statusClass}${isOverdue ? ' overdue' : ''}`}>
                  {m.status === 'completed' && m.completed_date ? '✓ ' : ''}
                  {new Date(displayDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
            <span className={`milestone-badge ${statusClass}`}>
              {m.status.replace('_', ' ')}
              {!isUpdating && (
                <span className="milestone-badge-next">
                  {' '}→ {STATUS_CYCLE[(STATUS_CYCLE.indexOf(m.status) + 1) % STATUS_CYCLE.length].replace('_', ' ')}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Journey Detail View
// ---------------------------------------------------------------------------
export function JourneyDetailView({
  transactionId,
  // Who is looking, for the condo documents panel: an explicit role, or the
  // viewer's person id matched against the transaction's buyer/seller.
  viewerId,
  viewerRole,
  onBack,
  onNavigateToContingencies,
  onNavigateToDocuments,
  onNavigateToClosing,
}) {
  const { journey, loading, error, reload } = useJourneyDetail(transactionId);
  const { milestones: fetchedMilestones, reload: reloadMilestones } = useJourneyMilestones(transactionId);
  const [localMilestones, setLocalMilestones] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [initializing, setInitializing] = useState(false);

  // Sync localMilestones from server data
  useEffect(() => {
    setLocalMilestones(fetchedMilestones);
  }, [fetchedMilestones]);

  const handleToggleMilestone = useCallback(
    async (milestone) => {
      if (updatingId) return;
      const curIdx = STATUS_CYCLE.indexOf(milestone.status);
      const nextStatus = STATUS_CYCLE[(curIdx + 1) % STATUS_CYCLE.length];
      // Optimistic update
      setLocalMilestones((prev) =>
        prev.map((m) => (m.id === milestone.id ? { ...m, status: nextStatus } : m))
      );
      setUpdatingId(milestone.id);
      try {
        await updateMilestone(milestone.id, { status: nextStatus });
        reloadMilestones();
        reload();
      } catch (err) {
        console.error('Failed to update milestone:', err);
        // Revert on error
        setLocalMilestones((prev) =>
          prev.map((m) => (m.id === milestone.id ? { ...m, status: milestone.status } : m))
        );
      } finally {
        setUpdatingId(null);
      }
    },
    [updatingId, reload, reloadMilestones]
  );

  const handleInitializeJourney = useCallback(async () => {
    setInitializing(true);
    try {
      await initializeJourney(transactionId);
      reloadMilestones();
    } catch (err) {
      console.error('Failed to initialize journey:', err);
    } finally {
      setInitializing(false);
    }
  }, [transactionId, reloadMilestones]);

  if (loading) {
    return (
      <div className="journey-detail">
        <button className="journey-back-btn" onClick={onBack} type="button">← Back to Journeys</button>
        <div className="journey-loading">
          <div className="journey-spinner" aria-label="Loading" />
          <p>Loading journey details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="journey-detail">
        <button className="journey-back-btn" onClick={onBack} type="button">← Back to Journeys</button>
        <div className="journey-error">{error}</div>
      </div>
    );
  }

  if (!journey) return null;

  const milestonesCompleted = localMilestones.filter((m) => m.status === 'completed').length;
  const contingenciesCleared = journey.contingencies_cleared ?? 0;
  const contingenciesTotal = journey.contingencies_total ?? 0;
  const closingDone = journey.closing_items_done ?? 0;
  const closingTotal = journey.closing_items_total ?? 0;

  return (
    <div className="journey-detail">
      <button className="journey-back-btn" onClick={onBack} type="button">← Back to Journeys</button>

      {/* Header */}
      <header className="journey-detail-header">
        <h2 className="journey-detail-address">{journey.property_address || 'Property'}</h2>
        <div className="journey-detail-meta">
          {journey.price && <span className="journey-meta-item">💰 ${Number(journey.price).toLocaleString()}</span>}
          {journey.buyer_name && <span className="journey-meta-item">🧑 {journey.buyer_name}</span>}
          {journey.seller_name && <span className="journey-meta-item">🏠 {journey.seller_name}</span>}
        </div>
      </header>

      {/* Stage Stepper */}
      <StageStepper currentStage={journey.current_stage} />

      {/* Next Action */}
      <NextActionBanner nextAction={journey.next_action} />

      {/* Older offer with a selected contingency but no deadline: flagged for
          both parties, never computed (legal §9.8b item 3). */}
      <ContingencyDeadlineReviewNotice review={journey.contingency_deadline_review} />

      {/* Wis. Stat. § 703.33 condominium documents (condo transactions only) */}
      {condoDocumentsApply(journey) && (
        <CondoDocumentsPanel
          transactionId={transactionId}
          viewerRole={resolveViewerRole({ viewerRole, viewerId, transaction: journey.transaction })}
        />
      )}

      {/* Progress Overview */}
      <section className="progress-cards" aria-label="Progress overview">
        <ProgressCard label="Milestones" completed={milestonesCompleted} total={localMilestones.length} accentClass="accent-coral" />
        <ProgressCard label="Contingencies" completed={contingenciesCleared} total={contingenciesTotal} accentClass="accent-teal" />
        <ProgressCard label="Closing Items" completed={closingDone} total={closingTotal} accentClass="accent-indigo" />
      </section>

      {/* Milestones Timeline */}
      <section className="journey-section">
        <h3 className="journey-section-title">Milestones</h3>
        <MilestoneTimeline
          milestones={localMilestones}
          updatingId={updatingId}
          onToggle={handleToggleMilestone}
          onInitialize={handleInitializeJourney}
          initializing={initializing}
        />
      </section>

      {/* Quick Actions */}
      <section className="journey-quick-actions" aria-label="Quick actions">
        {onNavigateToContingencies && (
          <button className="journey-action-btn" onClick={onNavigateToContingencies} type="button">
            🔍 View Contingencies
          </button>
        )}
        {onNavigateToDocuments && (
          <button className="journey-action-btn" onClick={onNavigateToDocuments} type="button">
            📄 View Documents
          </button>
        )}
        {onNavigateToClosing && (
          <button className="journey-action-btn" onClick={() => onNavigateToClosing(transactionId)} type="button">
            📋 View Closing Checklist
          </button>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Property Timeline View (pre-transaction detail)
// ---------------------------------------------------------------------------
function PropertyTimelineView({
  propertyJourney,
  onBack,
  onNavigateToOffers,
  onScheduleTour,
  onViewHouse,
}) {
  if (!propertyJourney) return null;

  const { timeline_events, current_stage, property_address, price, status_detail, tour, offer, house_id } = propertyJourney;

  return (
    <div className="journey-detail">
      <button className="journey-back-btn" onClick={onBack} type="button">← Back to Journeys</button>

      <header className="journey-detail-header">
        <h2 className="journey-detail-address">{property_address || 'Property'}</h2>
        <div className="journey-detail-meta">
          {price && <span className="journey-meta-item">💰 ${Number(price).toLocaleString()}</span>}
          <span className={`journey-status-badge status-${status_detail}`}>{status_detail}</span>
        </div>
      </header>

      <StageStepper currentStage={current_stage} />

      {/* Timeline Events */}
      <section className="journey-section">
        <h3 className="journey-section-title">Activity Timeline</h3>
        {timeline_events && timeline_events.length > 0 ? (
          <div className="property-timeline" role="list" aria-label="Property timeline">
            {timeline_events.map((event, i) => (
              <div key={i} className={`timeline-event event-${event.type}`} role="listitem">
                <span className="timeline-event-dot" aria-hidden="true" />
                <div className="timeline-event-body">
                  <span className="timeline-event-label">{event.label}</span>
                  <span className="timeline-event-date">
                    {new Date(event.date).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="milestone-empty">No activity recorded yet.</p>
        )}
      </section>

      {/* Quick Actions based on current stage */}
      <section className="journey-quick-actions" aria-label="Quick actions">
        {!tour && (
          <button
            className="journey-action-btn"
            onClick={() => {
              if (onScheduleTour && house_id) {
                onScheduleTour(house_id);
              } else if (onViewHouse && house_id) {
                onViewHouse(house_id);
              }
            }}
            type="button"
            disabled={!house_id}
          >
            📅 Schedule a Tour
          </button>
        )}
        {!offer && (
          <button className="journey-action-btn" onClick={onNavigateToOffers} type="button">
            📝 Make an Offer
          </button>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Property Journey Card
// ---------------------------------------------------------------------------
function PropertyJourneyCard({ journey, onClick }) {
  const stageIdx = getStageIndex(journey.current_stage);
  const pct = Math.round((stageIdx / (STAGES.length - 1)) * 100);

  const statusBadgeClass = journey.offer?.status === 'rejected' || journey.offer?.status === 'expired'
    ? 'badge-inactive'
    : journey.offer?.status === 'accepted'
    ? 'badge-success'
    : '';

  return (
    <button className="journey-card" onClick={onClick} type="button" aria-label={`View journey for ${journey.property_address || 'property'}`}>
      <div className="journey-card-top">
        <h3 className="journey-card-address">{journey.property_address || 'Property'}</h3>
        <span className={`journey-card-badge stage-${journey.current_stage} ${statusBadgeClass}`}>
          {STAGES[stageIdx]?.icon} {STAGES[stageIdx]?.label}
        </span>
      </div>
      {journey.price && (
        <p className="journey-card-price">${Number(journey.price).toLocaleString()}</p>
      )}
      {journey.status_detail && journey.status_detail !== 'saved' && (
        <span className={`journey-card-status status-${journey.status_detail}`}>
          {journey.status_detail}
        </span>
      )}
      <div className="journey-card-progress">
        <div className="journey-card-progress-track">
          <div className="journey-card-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="journey-card-progress-label">{pct}% complete</span>
      </div>
      {journey.first_interaction_at && (
        <span className="journey-card-date">
          Since{' '}
          <time dateTime={new Date(journey.first_interaction_at).toISOString()}>
            {new Date(journey.first_interaction_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </time>
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// SellerJourneyList — the seller's sales under contract
// ---------------------------------------------------------------------------
function SellerTransactionCard({ journey, onClick }) {
  const stageIdx = getStageIndex(journey.current_stage);
  const pct = Math.round((stageIdx / (STAGES.length - 1)) * 100);
  const price = journey.offer_amount ?? journey.price;
  // A deal recorded as ended keeps the stage it reached; say it ended instead
  // of showing it as still in progress (QA 2026-10-16).
  const ended = (journey.transaction?.status || '').toLowerCase() === 'terminated';
  return (
    <button
      className="journey-card"
      onClick={onClick}
      type="button"
      aria-label={`View journey for ${journey.property_address || 'property'}`}
    >
      <div className="journey-card-top">
        <h3 className="journey-card-address">{journey.property_address || 'Property'}</h3>
        {ended ? (
          <span className="journey-card-badge badge-inactive" data-testid="seller-journey-ended">
            Ended
          </span>
        ) : (
          <span className={`journey-card-badge stage-${journey.current_stage}`}>
            {STAGES[stageIdx]?.icon} {STAGES[stageIdx]?.label}
          </span>
        )}
      </div>
      {price ? <p className="journey-card-price">${Number(price).toLocaleString()}</p> : null}
      {!ended && (
        <div className="journey-card-progress">
          <div className="journey-card-progress-track">
            <div className="journey-card-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="journey-card-progress-label">{pct}% complete</span>
        </div>
      )}
    </button>
  );
}

export function SellerJourneyList({
  sellerId,
  onNavigateToListings,
  onNavigateToContingencies,
  onNavigateToDocuments,
  onNavigateToClosing,
}) {
  const { journeys, loading, error } = useSellerJourneys(sellerId);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  if (selectedTransactionId) {
    return (
      <JourneyDetailView
        transactionId={selectedTransactionId}
        viewerId={sellerId}
        viewerRole="seller"
        onBack={() => setSelectedTransactionId(null)}
        onNavigateToContingencies={onNavigateToContingencies}
        onNavigateToDocuments={onNavigateToDocuments}
        onNavigateToClosing={onNavigateToClosing}
      />
    );
  }

  return (
    <div className="journey-container" data-testid="seller-journey-list">
      <h2 className="journey-heading">Your Sale Journey</h2>
      {loading && <p className="journey-subheading">Loading…</p>}
      {!loading && error && <div className="journey-error">{error}</div>}
      {!loading && !error && journeys.length === 0 && (
        <div className="journey-empty">
          <span className="journey-empty-icon" aria-hidden="true">🏡</span>
          <p className="journey-empty-text">
            No sales underway yet. When you accept an offer, its milestones — through closing — will appear here.
          </p>
          {onNavigateToListings && (
            <button className="journey-empty-btn" onClick={onNavigateToListings} type="button">
              Go to My Listings
            </button>
          )}
        </div>
      )}
      {!loading && !error && journeys.length > 0 && (
        <div className="journey-list">
          {journeys.map((j) => (
            <SellerTransactionCard
              key={j.transaction_id}
              journey={j}
              onClick={() => setSelectedTransactionId(j.transaction_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TransactionJourney — Main Component
// ---------------------------------------------------------------------------
const TransactionJourney = ({
  buyerId,
  role,
  onNavigateToListings,
  onNavigateToOffers,
  onNavigateToContingencies,
  onNavigateToDocuments,
  onNavigateToClosing,
  onScheduleTour,
  onViewHouse,
}) => {
  const isSeller = role === 'seller';
  // The buyer journey (favorites, tours, offers) means nothing on the seller
  // side; skip its fetch there.
  const { propertyJourneys, loading, error } = usePropertyJourneys(isSeller ? null : buyerId);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedPropertyJourney, setSelectedPropertyJourney] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [dismissedError, setDismissedError] = useState(false);
  // A newly arriving error should surface even if a previous one was dismissed.
  useEffect(() => {
    if (error) setDismissedError(false);
  }, [error]);

  const sortedJourneys = useMemo(() => {
    return [...(propertyJourneys || [])].sort(
      (a, b) => getJourneySortTimestamp(b) - getJourneySortTimestamp(a)
    );
  }, [propertyJourneys]);

  useEffect(() => {
    setCurrentPage(1);
  }, [propertyJourneys]);

  const totalPages = Math.max(1, Math.ceil(sortedJourneys.length / JOURNEYS_PER_PAGE));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const pageStart = (currentPageSafe - 1) * JOURNEYS_PER_PAGE;
  const paginatedJourneys = sortedJourneys.slice(pageStart, pageStart + JOURNEYS_PER_PAGE);

  if (isSeller) {
    return (
      <SellerJourneyList
        sellerId={buyerId}
        onNavigateToListings={onNavigateToListings}
        onNavigateToContingencies={onNavigateToContingencies}
        onNavigateToDocuments={onNavigateToDocuments}
        onNavigateToClosing={onNavigateToClosing}
      />
    );
  }

  // Drill-down: transaction detail view (for accepted offers with transactions)
  if (selectedTransactionId) {
    return (
      <JourneyDetailView
        transactionId={selectedTransactionId}
        viewerId={buyerId}
        onBack={() => setSelectedTransactionId(null)}
        onNavigateToContingencies={onNavigateToContingencies}
        onNavigateToDocuments={onNavigateToDocuments}
        onNavigateToClosing={onNavigateToClosing}
      />
    );
  }

  // Drill-down: property timeline view (for pre-transaction interactions)
  if (selectedPropertyJourney) {
    return (
      <PropertyTimelineView
        propertyJourney={selectedPropertyJourney}
        onBack={() => setSelectedPropertyJourney(null)}
        onNavigateToOffers={onNavigateToOffers}
        onScheduleTour={onScheduleTour}
        onViewHouse={onViewHouse}
      />
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="journey-container">
        <h2 className="journey-heading">Your Home Journey</h2>
        <div className="journey-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="journey-card journey-card-skeleton" aria-hidden="true">
              <div className="skeleton-line skeleton-title" />
              <div className="skeleton-line skeleton-badge" />
              <div className="skeleton-line skeleton-price" />
              <div className="skeleton-line skeleton-bar" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // A load error with nothing else to show replaces the view. When journeys are
  // already present, the error renders as a dismissible inline banner over the
  // list (below) instead of blowing the whole list away.
  if (error && (!propertyJourneys || propertyJourneys.length === 0)) {
    return (
      <div className="journey-container">
        <h2 className="journey-heading">Your Home Journey</h2>
        <div className="journey-error">{error}</div>
      </div>
    );
  }

  // Empty state
  if (!propertyJourneys || propertyJourneys.length === 0) {
    return (
      <div className="journey-container">
        <h2 className="journey-heading">Your Home Journey</h2>
        <div className="journey-empty">
          <span className="journey-empty-icon" aria-hidden="true">🏡</span>
          <p className="journey-empty-text">
            No active transactions yet. Once a deal is underway, your milestones — from accepted offer through closing — will appear here.
          </p>
          {onNavigateToOffers && (
            <button className="journey-empty-btn" onClick={onNavigateToOffers} type="button">
              Browse Properties
            </button>
          )}
        </div>
      </div>
    );
  }

  // Journey list
  return (
    <div className="journey-container">
      <h2 className="journey-heading">Your Home Journey</h2>
      {error && !dismissedError && (
        <div className="journey-error-banner" role="alert">
          <span className="journey-error-banner__text">{error}</span>
          <button
            type="button"
            className="journey-error-banner__dismiss"
            aria-label="Dismiss error"
            onClick={() => setDismissedError(true)}
          >
            ×
          </button>
        </div>
      )}
      <p className="journey-subheading">{sortedJourneys.length} propert{sortedJourneys.length === 1 ? 'y' : 'ies'} tracked</p>
      <div className="journey-list-meta">
        <span>
          Showing {paginatedJourneys.length} of {sortedJourneys.length} propert{sortedJourneys.length === 1 ? 'y' : 'ies'}
        </span>
        <span className="journey-list-sort">Sorted: Newest → Oldest</span>
      </div>
      <div className="journey-list">
        {paginatedJourneys.map((j) => (
          <PropertyJourneyCard
            key={j.house_id}
            journey={j}
            onClick={() => {
              if (j.transaction) {
                setSelectedTransactionId(j.transaction.id);
              } else {
                setSelectedPropertyJourney(j);
              }
            }}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="journey-pagination" role="navigation" aria-label="Transaction journey pagination">
          <button
            type="button"
            className="journey-pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPageSafe === 1}
          >
            ← Previous
          </button>
          <span className="journey-pagination-info">Page {currentPageSafe} of {totalPages}</span>
          <button
            type="button"
            className="journey-pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPageSafe === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionJourney;
